import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { onSnapshot, types } from "mobx-state-tree";

/**
 * UI state for the sidebar "Building / Projects" view-mode toggle.
 *
 * Every building shell (`/buildings/:id/...`) renders in one of two view
 * modes: "building" (whole-building perspective — dashboard, timeline,
 * folders) or "projects" (single-project perspective — project dashboard,
 * timeline, folders, 3D model, PM). The mode changes which nav items are
 * shown and how pages scope their data.
 *
 * The store remembers each building's last-used mode; absence of a key means
 * the first-visit default ("building"). Persistence mirrors
 * `buildingDataWidgetStore` exactly: seed from localStorage via
 * `loadBuildingViewModeSnapshot()` and mirror snapshots back with
 * `connectBuildingViewModePersistence()` after the root store is built. The
 * MST model itself stays side-effect free.
 */

const STORAGE_KEY = "building-view-mode:v1";

export const BUILDING_VIEW_MODES = ["building", "projects"] as const;
export type BuildingViewMode = (typeof BUILDING_VIEW_MODES)[number];

const DEFAULT_VIEW_MODE: BuildingViewMode = "building";

export const BuildingViewModeStore = types
  .model("BuildingViewModeStore", {
    /**
     * Last-used view mode per building, keyed by `buildingId` (string). The
     * absence of a key means the default mode ("building"), so first-time
     * visitors land on the building view with no special-casing.
     */
    viewModeByBuilding: types.map(types.enumeration<BuildingViewMode>([...BUILDING_VIEW_MODES])),
  })
  .views((self) => ({
    /** The view mode for a building; defaults to "building" when unset. */
    getViewMode(buildingId: string): BuildingViewMode {
      return self.viewModeByBuilding.get(buildingId) ?? DEFAULT_VIEW_MODE;
    },
  }))
  .actions((self) => ({
    /** Remember the view mode for a building. */
    setViewMode(buildingId: string, mode: BuildingViewMode) {
      self.viewModeByBuilding.set(buildingId, mode);
    },
  }));

export type BuildingViewModeStoreInstance = Instance<typeof BuildingViewModeStore>;
export type BuildingViewModeStoreSnapshotIn = SnapshotIn<typeof BuildingViewModeStore>;
export type BuildingViewModeStoreSnapshotOut = SnapshotOut<typeof BuildingViewModeStore>;

/**
 * Read the initial snapshot for the view-mode store from localStorage.
 * Safe to call in non-browser environments — falls back to defaults.
 */
export function loadBuildingViewModeSnapshot(): BuildingViewModeStoreSnapshotIn {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<BuildingViewModeStoreSnapshotOut>;
    const map = parsed.viewModeByBuilding;
    if (!map || typeof map !== "object") return {};
    // Keep only well-formed string→mode entries; drop anything corrupt so a
    // bad persisted value can't crash MST snapshot application on load.
    const cleaned: Record<string, BuildingViewMode> = {};
    for (const [key, value] of Object.entries(map)) {
      if (value === "building" || value === "projects") {
        cleaned[key] = value;
      }
    }
    return { viewModeByBuilding: cleaned };
  } catch {
    return {};
  }
}

/**
 * Subscribe to view-mode-store snapshots and mirror them into localStorage.
 * Call once after the root store has been created.
 *
 * Writes are coalesced with a 200ms trailing debounce; `beforeunload` flushes
 * the latest snapshot so a value mid-debounce isn't lost when the tab closes.
 * Mirrors `connectBuildingDataWidgetPersistence`.
 */
export function connectBuildingViewModePersistence(
  store: BuildingViewModeStoreInstance
): () => void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return () => undefined;
  }

  let pending: BuildingViewModeStoreSnapshotOut | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!pending) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } catch {
      // Quota / privacy-mode failures are non-fatal for a purely-UI preference.
    }
    pending = null;
  };

  const dispose = onSnapshot(store, (snapshot) => {
    pending = snapshot;
    if (timer) return;
    timer = setTimeout(flush, 200);
  });

  window.addEventListener("beforeunload", flush);

  return () => {
    flush();
    window.removeEventListener("beforeunload", flush);
    dispose();
  };
}
